import { array, concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import { importSync } from '@embroider/macros';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { recordIdentifierFor } from '@warp-drive/core';
import can from 'ember-can/helpers/can';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, notEq } from 'ember-truth-helpers';

import Newsletter from 'umg-ember-backend/components/record-detail/forms/story/content/newsletter/index';
import Button from 'umg-ember-backend/components/ui/button/index';
import ContextMenu from 'umg-ember-backend/components/ui/context-menu/index';
import Modal from 'umg-ember-backend/components/ui/modal/index';
import { findRecord } from 'umg-ember-backend/data/builders/umg-rest';
import modelIcon from 'umg-ember-backend/helpers/model-icon';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import styles from './styles.module.css';

const bem = getBem(styles);

const hasItem = (items, type) => {
  return items.some((item) => item.type === type);
};
export default class RecordDetailStoryContentComponent extends Component {
  // Services
  @service breadcrumb;
  @service flashMessages;
  @service history;
  @service router;
  @service intl;
  @service recordStatus;
  @service store;

  // Defaults
  @storeOnRoute('routeStorageKey') scrollPositionAssign;
  @storeOnRoute('routeStorageKey') relationNameToAssign = null;
  @tracked selectedNewsletter = null;

  // Getter and setter
  get routeStorageKey() {
    return recordIdentifierFor(this.args.story).lid;
  }

  get isDisabled() {
    return (
      this.recordStatus.isDisabled(this.args.story) ||
      !this.args.form.hasPermissionToEdit
    );
  }

  get RecordExplorerForAssign() {
    const type = this.relationNameToAssign ?? 'article';

    const module = importSync(`../../../../record-explorer/for/${type}`);

    return module.default;
  }

  // Functions
  addLiveEventFeed = () => {
    this.args.story.storyContents = [
      ...this.args.story.storyContents,
      {
        id: this.args.story.id,
        type: 'live-event-feed',
        headline: 'LiveEventFeed',
        showMore: false,
      },
    ];
  };

  addNewsfeed = () => {
    this.args.story.storyContents = [
      ...this.args.story.storyContents,
      {
        id: this.args.story.id,
        type: 'newsfeed',
        headline: 'Newsfeed',
        showMore: false,
      },
    ];
  };

  addNewsletter = async () => {
    let newRecord = await this.store.createRecord('newsletter');

    this.selectedNewsletter = newRecord;
  };

  saveNewsletter = async (newsletter) => {
    try {
      let isNew = newsletter.isNew;

      await this.history.saveModel(newsletter);

      if (isNew) {
        this.args.story.storyContents = [
          ...this.args.story.storyContents,
          {
            id: newsletter.get('id'),
            type: getRecordType(newsletter),
            headline: 'Newsletter',
            showMore: false,
          },
        ];
      }

      this.selectedNewsletter = null;
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  };

  assignRecords = (records) => {
    records.forEach((record) => {
      this.args.story.storyContents = [
        ...this.args.story.storyContents,
        {
          id: record.get('id'),
          type: getRecordType(record),
          headline: record.get('headline'),
          subType: record.get('entitySubtype'),
          showMore: false,
        },
      ];
    });

    this.closeRecordList();
  };

  openRecord = async (content) => {
    const { content: result } = await this.store.request(
      findRecord(content.type, content.id),
    );
    let loadedRecord = result.data;

    if (content.type === 'newsletter') {
      this.selectedNewsletter = loadedRecord;
    } else {
      let route = this.breadcrumb.registerDetailRoute(loadedRecord);
      this.router.transitionTo(...route.transitionToParams);
    }
  };

  closeRecordList = () => {
    this.relationNameToAssign = null;
    this.scrollPositionAssign = 0;
  };

  openRecordList = (modelTypeName) => {
    this.relationNameToAssign = dasherize(modelTypeName);
  };

  remove = (item) => {
    const index = this.args.story.storyContents.indexOf(item);
    if (index !== -1) {
      this.args.story.storyContents.splice(index, 1);
      this.args.story.storyContents = [...this.args.story.storyContents];
    }
  };

  reorderItems = (items) => {
    this.args.story.storyContents = items;
  };

  // Template
  <template>
    <div class={{(bem)}}>
      <div class={{bem 'actions'}}>
        <ContextMenu @isDisabled={{this.isDisabled}}>
          <:trigger>
            <Button
              @label={{t 'story_content.assignNew'}}
              @icon='add'
              @style='outline'
              disabled={{this.isDisabled}}
            />
          </:trigger>

          <:content as |content|>
            {{#each
              (array
                'action'
                'artist'
                'article'
                'asset'
                'collection'
                'teaser'
                'product'
                'product-family'
                'audioplayer'
                'media-embed'
                'media-event'
                'live-event'
              )
              as |recordType|
            }}
              <content.Item
                @icon={{modelIcon recordType}}
                @label={{t (concat 'models.' recordType)}}
                @onClick={{fn this.openRecordList recordType}}
              />
            {{/each}}

            <content.Item
              @icon={{modelIcon 'mlp-news-feed'}}
              @isDisabled={{hasItem @story.storyContents 'newsfeed'}}
              @label={{t (concat 'models.news-feed')}}
              @onClick={{this.addNewsfeed}}
            />

            <content.Item
              @icon={{modelIcon 'mlp-live-event-feed'}}
              @isDisabled={{hasItem @story.storyContents 'live-event-feed'}}
              @label={{t (concat 'models.mlp-live-event-feed')}}
              @onClick={{this.addLiveEventFeed}}
            />

            <content.Item
              @icon={{modelIcon 'newsletter'}}
              @label={{t (concat 'models.newsletter')}}
              @onClick={{this.addNewsletter}}
            />
          </:content>
        </ContextMenu>
      </div>

      <table class='o-table'>
        <thead class='o-table__head'>
          <tr class='o-table__row'>
            <th class='o-table__cell'></th>
            <th class='o-table__cell'>
              {{t 'story_content.id'}}
            </th>
            <th class='o-table__cell'>
              {{t 'story_content.entity'}}
            </th>
            <th class='o-table__cell'>
              {{t 'story_content.headline'}}
            </th>
            <th class='o-table__cell'></th>
          </tr>
        </thead>
        <tbody
          class='o-table__body'
          {{sortableGroup groupName='story' onChange=this.reorderItems}}
        >
          {{#each @story.storyContents as |content|}}
            <tr
              class='o-table__row'
              {{sortableItem groupName='story' model=content}}
            >
              <td class='o-table__cell {{bem "handle"}}' {{sortableHandle}}>
                {{svgJar 'drag' class=(bem 'drag-indicator')}}
              </td>
              <td class='o-table__cell'>
                {{content.id}}
              </td>
              <td class='o-table__cell'>
                {{t (concat 'models.' (dasherize content.type))}}

                {{#if content.subType}}
                  {{#if (notEq content.type 'collection')}}
                    ({{content.subType}})
                  {{else}}
                    ({{t (concat 'models.' (dasherize content.subType))}})
                  {{/if}}
                {{/if}}
              </td>
              <td class='o-table__cell'>
                {{content.headline}}
              </td>
              <td class='{{bem "row-actions"}} o-table__cell u-align-right'>
                {{#if
                  (and
                    (notEq content.type 'newsfeed')
                    (notEq content.type 'live-event-feed')
                  )
                }}
                  <Button
                    @icon='edit'
                    @style='outline'
                    {{on 'click' (fn this.openRecord content)}}
                  />
                {{/if}}

                {{#if (can 'update' @story)}}
                  <Button
                    @icon='delete'
                    @style='outline'
                    class='u-color-error'
                    {{on 'click' (fn this.remove content)}}
                  />
                {{/if}}
              </td>
            </tr>
          {{/each}}
        </tbody>
      </table>

      <Modal
        @onClose={{this.closeRecordList}}
        @dataDependencies={{this.relationNameToAssign}}
        as |relationNameToAssign|
      >
        <this.RecordExplorerForAssign
          @filters={{@filters}}
          @onAssignRelation={{this.assignRecords}}
          @recordType={{relationNameToAssign}}
          @parentRecordType={{getRecordType @story}}
          @relationType='belongsTo'
          @onBodyScroll={{fn (mut this.scrollPositionAssign)}}
          @scrollPosition={{this.scrollPositionAssign}}
        />
      </Modal>

      <Newsletter
        @record={{this.selectedNewsletter}}
        @onCloseEditing={{fn (mut this.selectedNewsletter) null}}
        @onSaveNewsletter={{this.saveNewsletter}}
        @story={{@story}}
      />
    </div>
  </template>
}
